import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CallMethodProps, VideoAppCallMethodProps } from 'types/med'
import { CallMethodNames, CallMethodVideoAppNames } from 'types/common'
import { Button, MenuItem, Select, Switch } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material'
import { StackItem } from 'ui/layout/StackItem'
import { Caption, Text1 } from 'ui/shorthands/Texts'
import { VideoAppSettingCaption } from 'ui/partials/VideoAppDescription'
import {
  ApiErrorWithMessage,
  ApiErrorWithNoMessage,
  NotFoundError,
} from 'app/client'

const initialSettings: SettingsObj = {
  phone: {
    name: 'phone',
    label: '電話',
    is_active: true,
  },
  video_teams: {
    name: 'video_teams',
    label: 'Microsoft Teams',
    is_active: false,
  },
  video_zoom: {
    name: 'video_zoom',
    label: 'Zoom',
    is_active: false,
  },
}

const callMethodArrToObj: (array: CallMethodProps[]) => {
  [key in CallMethodNames]?: CallMethodProps
} = (array) => {
  return {
    phone: array.find((m) => m.name === 'phone') || initialSettings.phone,
    video_teams:
      array.find((m) => m.name === 'video_teams') ||
      initialSettings.video_teams,
    video_zoom:
      array.find((m) => m.name === 'video_zoom') || initialSettings.video_zoom,
  }
}

const useStyles = makeStyles()((theme: Theme) => ({
  connectedLabel: {
    lineHeight: 2.25,
  },
  notConnectedLabel: {
    lineHeight: 3.5,
  },
  switchWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  videoAppName: {
    display: 'inline-block',
    marginRight: '1em',
  },
  videoSetting: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  videoSettingCaption: {
    paddingTop: theme.spacing(1),
  },
  select: {
    width: 440,
  },
}))

type SettingsObj = { [key in CallMethodNames]?: CallMethodProps }

export type ConnectAppPromise = (
  app: CallMethodVideoAppNames
) => Promise<() => void | { message: string }>

export type CallMethodEditProps = {
  /**
   * call_methods配列
   */
  value: CallMethodProps[]
  /**
   * 内容を変更した時の関数
   */
  onChange: (methods: CallMethodProps[]) => void
  /**
   * ビデオアプリを接続した時の関数
   */
  onClickConnect: (app: CallMethodVideoAppNames) => Promise<void>
  /**
   * ビデオアプリ接続解除した時の関数
   */
  onClickDisConnect: (app: CallMethodVideoAppNames) => Promise<void>
  /**
   * 通話未完了の予約があるかどうか
   */
  hasUnFinishedCallReservation?: boolean
  /**
   * 接続結果
   */
  connectVideoAppResult?: CallMethodVideoAppNames | { error: string }
}

export const CallMethodEdit: React.FC<CallMethodEditProps> = ({
  value,
  onChange,
  onClickConnect,
  onClickDisConnect,
  hasUnFinishedCallReservation,
  connectVideoAppResult,
}: CallMethodEditProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const [communicating, setCommunicating] = useState<boolean>(false)
  const [videoAppConnected, setVideoAppConnected] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>()

  const [videoApp, setVideoApp] =
    useState<CallMethodVideoAppNames>('video_teams')

  const [{ phone, video_teams, video_zoom }, setCallMethodsObj] =
    useState<SettingsObj>(initialSettings)

  useEffect(() => {
    setCallMethodsObj(callMethodArrToObj(value))
    setErrorMessage(undefined)
    setCommunicating(false)
  }, [value])

  useEffect(() => {
    setVideoAppConnected(!!(video_teams?.is_active || video_zoom?.is_active))
  }, [phone, video_teams, video_zoom])

  useEffect(() => {
    // TODO: PromiseでOAuthの結果が待てればここは削除
    // NOTE: ここで接続処理が非同期に完了
    setCommunicating(false)
    setErrorMessage(undefined)

    if (!phone || !video_teams || !video_zoom) {
      return
    }

    if (
      typeof connectVideoAppResult !== 'string' &&
      connectVideoAppResult?.error
    ) {
      setErrorMessage(connectVideoAppResult.error)
      return
    } else {
      setErrorMessage(undefined)
    }

    switch (connectVideoAppResult) {
      case 'video_zoom':
        onChange([
          phone,
          { ...video_teams, is_active: false },
          { ...video_zoom, is_active: true },
        ])
        break
      case 'video_teams':
        onChange([
          phone,
          { ...video_teams, is_active: true },
          { ...video_zoom, is_active: false },
        ])
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectVideoAppResult])

  const connectHandler = (
    app: CallMethodVideoAppNames,
    connect: (app: CallMethodVideoAppNames) => Promise<void>
  ) => {
    if (!video_teams || !video_zoom) {
      return
    }
    setErrorMessage(undefined)
    setCommunicating(true)
    const result = connect(app)

    const onDisconnect = () => {
      onChange([
        // NOTE: 接続解除した時は連絡事項は空なので必ずphoneはtrueになる
        { ...(phone as CallMethodProps), is_active: true },
        { ...video_teams, is_active: false },
        { ...video_zoom, is_active: false },
      ])
    }

    result
      .then(() => {
        switch (connect.name) {
          case 'onClickConnectVideoApp':
            // TODO: OAuthの結果が待てればこうしたい
            //
            // onChange([
            //   phone,
            //   {
            //     ...video_teams,
            //     is_active: app === 'video_teams',
            //   },
            //   { ...video_zoom, is_active: app === 'video_zoom' },
            // ])
            break
          case 'onClickDisconnectVideoApp':
            if (communicating) {
              // NOTE: 削除APIを多重に呼ばないように
              break
            }
            onDisconnect()
            break
        }
      })
      .catch((e) => {
        // NOTE: 多重操作などで解除済みだった場合
        if (e instanceof NotFoundError) {
          onDisconnect()
          return Promise.resolve()
        }
        if (e instanceof ApiErrorWithMessage) {
          setErrorMessage(t('errors.接続解除に失敗しました。'))
          return
        }
        if (e instanceof ApiErrorWithNoMessage) {
          setErrorMessage(t('errors.接続解除に失敗しました。'))
        }
      })
      .finally(() => {
        // NOTE: onClickConnectの時はOAuthウィンドウからcodeをAPIでサーバに送った結果を待つので通信中のまま
        if (connect.name === 'onClickDisconnectVideoApp') {
          setCommunicating(false)
        }
      })
  }

  const togglePhoneIsActive = () => {
    if (!phone || !video_teams || !video_zoom) {
      return
    }
    const newSetting: CallMethodProps[] = [
      { ...phone, is_active: !phone?.is_active },
      video_teams,
      video_zoom,
    ]
    onChange(newSetting)
  }

  const ConnectedVideo: React.FC<{ app: VideoAppCallMethodProps }> = ({
    app,
  }) => {
    const { t } = useTranslation()
    return (
      <StackItem
        label={
          <span className={classes.connectedLabel}>{t('ビデオ通話')}</span>
        }
        alignTop
        description={errorMessage && <Caption error>{errorMessage}</Caption>}
      >
        <div className={classes.videoSetting}>
          <Text1>
            <span className={classes.videoAppName}>{app.label}</span>
            {app?.identification && `ID: ${app.identification}`}
          </Text1>
          <Button
            variant={'contained'}
            color={'primary'}
            onClick={() =>
              connectHandler(
                app.name as CallMethodVideoAppNames,
                onClickDisConnect
              )
            }
            disabled={communicating || hasUnFinishedCallReservation}
          >
            {t('接続解除')}
          </Button>
        </div>
        <div className={classes.videoSettingCaption}>
          <VideoAppSettingCaption app={app.name} connected={true} />
        </div>
      </StackItem>
    )
  }

  const NotConnectedVideo: React.FC = () => {
    const { t } = useTranslation()
    return (
      <StackItem
        label={
          <span className={classes.notConnectedLabel}>{t('ビデオ通話')}</span>
        }
        alignTop
        description={errorMessage && <Caption error>{errorMessage}</Caption>}
      >
        <div className={classes.videoSetting}>
          <Select
            variant="outlined"
            displayEmpty
            className={classes.select}
            value={videoApp}
            onChange={(e) => {
              if (
                e.target.value === 'video_teams' ||
                e.target.value === 'video_zoom'
              ) {
                setVideoApp(e.target.value)
              }
            }}
          >
            {video_teams && (
              <MenuItem key={'select-item-video_teams'} value={'video_teams'}>
                {t(video_teams.label)}
              </MenuItem>
            )}
            {video_zoom && (
              <MenuItem key={'select-item-video_zoom'} value={'video_zoom'}>
                {t(video_zoom.label)}
              </MenuItem>
            )}
          </Select>
          <Button
            variant={'contained'}
            color={'primary'}
            onClick={() =>
              connectHandler(
                videoApp as CallMethodVideoAppNames,
                onClickConnect
              )
            }
          >
            {t('接続する')}
          </Button>
        </div>
        <div className={classes.videoSettingCaption}>
          <VideoAppSettingCaption
            app={videoApp as CallMethodVideoAppNames}
            connected={false}
          />
        </div>
      </StackItem>
    )
  }

  return (
    <>
      {phone && (
        <StackItem label={t('電話')}>
          <div className={classes.switchWrapper}>
            <div>{t('使用しない')}</div>
            <Switch
              color="primary"
              disabled={communicating}
              checked={!!phone?.is_active}
              inputProps={{ 'aria-label': 'primary checkbox' }}
              onChange={togglePhoneIsActive}
            />
            <div>{t('使用する')}</div>
          </div>
        </StackItem>
      )}
      {!videoAppConnected && <NotConnectedVideo />}
      {video_teams && video_teams.is_active && (
        <ConnectedVideo app={video_teams as VideoAppCallMethodProps} />
      )}
      {video_zoom && video_zoom.is_active && (
        <ConnectedVideo app={video_zoom as VideoAppCallMethodProps} />
      )}
    </>
  )
}

export const CallMethodRead: React.FC<{
  callMethods: CallMethodProps[]
}> = ({ callMethods }) => {
  const { t } = useTranslation()
  const phone = callMethods.find((m) => m.name === 'phone')
  const activeVideoApp = callMethods.find(
    (m) => m.name !== 'phone' && m.is_active
  )
  return (
    <>
      <StackItem label={t('電話')}>
        <Text1>{phone?.is_active ? t('使用する') : t('使用しない')}</Text1>
      </StackItem>
      <StackItem label={t('ビデオ通話')}>
        <Text1>
          {activeVideoApp?.is_active
            ? `${activeVideoApp.label ? t(activeVideoApp.label) : null}（${
                activeVideoApp.identification
              }）`
            : t('使用しない')}
        </Text1>
      </StackItem>
    </>
  )
}
